import { useState, useEffect, useMemo } from 'react'

interface CountdownTime {
  days: number
  hours: number
  minutes: number
  seconds: number
  total: number // Total milliseconds remaining
  isExpired: boolean
}

/**
 * Custom hook for countdown timer
 * @param targetDate - Target date string in ISO format (with timezone) or Date object
 * @param timezone - Timezone string (deprecated - timezone should be in targetDate)
 * @returns CountdownTime object with remaining time
 */
export function useCountdown(
  targetDate: string | Date | null,
  timezone?: string // Deprecated - timezone should be included in targetDate string
): CountdownTime {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0,
    isExpired: true,
  })

  useEffect(() => {
    if (!targetDate) {
      setTimeLeft({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        total: 0,
        isExpired: true,
      })
      return
    }

    // Parse target date
    let target: Date
    if (typeof targetDate === 'string') {
      // If it's an ISO string, parse it
      target = new Date(targetDate)
    } else {
      target = targetDate
    }

    // Calculate time difference
    const calculateTimeLeft = () => {
      const now = new Date()
      const difference = target.getTime() - now.getTime()

      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          total: 0,
          isExpired: true,
        }
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        total: difference,
        isExpired: false,
      }
    }

    // Set initial time
    setTimeLeft(calculateTimeLeft())

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(timer)
  }, [targetDate, timezone])

  return timeLeft
}

// Memoized launch date to prevent re-parsing on every render
let cachedLaunchDate: Date | null | undefined = undefined
let hasLoggedLaunchDate = false

/**
 * Get target date from environment variable
 * 
 * Format: Full ISO 8601 string with timezone offset
 * Examples:
 *   - "2025-02-05T16:00:00+04:00" (Dubai time, GMT+4)
 *   - "2025-02-05T16:00:00+05:00" (Kazakhstan time, GMT+5)
 *   - "2025-02-05T16:00:00Z" (UTC)
 *   - "2025-02-05T16:00:00+00:00" (UTC)
 * 
 * The timezone offset is REQUIRED to ensure absolute time.
 * Without it, the time would be interpreted in the user's local timezone.
 */
export function getVaultLaunchDate(): Date | null {
  // Return cached value if already parsed
  if (cachedLaunchDate !== undefined) {
    return cachedLaunchDate
  }

  const launchDateStr = import.meta.env.VITE_VAULT_LAUNCH_DATE

  if (!launchDateStr) {
    cachedLaunchDate = null
    if (!hasLoggedLaunchDate) {
      console.log('[Vault Launch] VITE_VAULT_LAUNCH_DATE not set - vault purchasing enabled (no countdown)')
      hasLoggedLaunchDate = true
    }
    return null
  }

  try {
    const dateStr = launchDateStr.trim()
    
    // Check if it already has a timezone (Z or +/-HH:MM)
    const hasTimezone = dateStr.includes('Z') || 
                       /[+-]\d{2}:\d{2}$/.test(dateStr) ||
                       /[+-]\d{4}$/.test(dateStr)
    
    if (hasTimezone) {
      // Full ISO 8601 string with timezone - use directly
      const parsedDate = new Date(dateStr)
      const now = new Date()
      cachedLaunchDate = parsedDate
      
      // Log only once when first parsed
      if (!hasLoggedLaunchDate) {
        console.log('[Vault Launch] VITE_VAULT_LAUNCH_DATE:', launchDateStr)
        console.log('[Vault Launch] Parsed launch date:', parsedDate.toISOString(), 'Current time:', now.toISOString(), 'Expired:', parsedDate.getTime() <= now.getTime())
        hasLoggedLaunchDate = true
      }
      
      return parsedDate
    }

    // If no timezone is specified, throw an error to force explicit timezone
    // This prevents ambiguity - user must specify the timezone
    cachedLaunchDate = null
    if (!hasLoggedLaunchDate) {
      console.error(
        'VITE_VAULT_LAUNCH_DATE must include timezone offset. ' +
        'Examples: "2025-02-05T16:00:00+04:00" (Dubai), "2025-02-05T16:00:00+05:00" (Kazakhstan)'
      )
      hasLoggedLaunchDate = true
    }
    return null
  } catch (error) {
    cachedLaunchDate = null
    if (!hasLoggedLaunchDate) {
      console.error('Invalid VITE_VAULT_LAUNCH_DATE format:', error)
      hasLoggedLaunchDate = true
    }
    return null
  }
}
